import { produce } from 'immer';
import { createReducer } from 'typesafe-actions';

import {
  createOneOffLedgerItemRequestAction,
  deletePayableAction,
  payableAddAdjustmentAction,
  payableAdjustmentDeleteAction,
  payableAdjustmentsByLedgerItemIdRequestAction,
  payableAdjustmentsRequestAction,
  payableByIdRequestAction,
  payableEventsByIdRequestAction,
  payableIgnoreAction,
  payableRequestAction,
  toggleAddAdjustmentsForm,
  toggleCreateOneOffLedgerItem,
  updateAddAdjustmentsForm,
} from './actions';
import type { PayableAction, PayableState } from './types';

export const initialPayableState: PayableState = {
  addAdjustmentsDrawerOpen: false,
  addAdjustmentsForm: {},
  adjustments: [],
  adjustmentsByLedgerItemId: {},
  byId: {},
  createOneOffDrawerOpen: false,
  eventsById: {},
  haveAllAdjustmentsBeenRequested: false,
  haveAllPayablesBeenRequested: false,
  rows: [],
};
export const payableReducer = createReducer<PayableState, PayableAction>(
  initialPayableState
)
  .handleAction(payableRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.haveAllPayablesBeenRequested = true;
      draftState.rows = payload;
    })
  )
  .handleAction(payableByIdRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.byId[payload.id] = payload;
    })
  )
  .handleAction(payableIgnoreAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      const updatedIndex = draftState.rows.findIndex(
        (p) => p.id === payload[0].id
      );
      const updatedPayable = {
        ...draftState.rows[updatedIndex],
        status: payload[0].status,
      };
      const updatedPayables = [...draftState.rows];

      updatedPayables[updatedIndex] = updatedPayable;
      draftState.rows = updatedPayables;
    })
  )
  .handleAction(payableAdjustmentsRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.haveAllAdjustmentsBeenRequested = true;
      draftState.adjustments = payload;
    })
  )
  .handleAction(
    payableAdjustmentsByLedgerItemIdRequestAction.success,
    (state, { payload: { ledgerItemId, adjustments } }) =>
      produce(state, (draftState) => {
        draftState.adjustmentsByLedgerItemId[ledgerItemId] = adjustments;
      })
  )
  .handleAction(payableAddAdjustmentAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.addAdjustmentsDrawerOpen =
        !draftState.addAdjustmentsDrawerOpen;
      draftState.adjustments = [...draftState.adjustments, ...payload];
      draftState.haveAllAdjustmentsBeenRequested = false;
      draftState.addAdjustmentsForm = {};
    })
  )
  .handleAction(toggleCreateOneOffLedgerItem, (state) =>
    produce(state, (draftState) => {
      draftState.createOneOffDrawerOpen = !draftState.createOneOffDrawerOpen;
    })
  )
  .handleAction(
    toggleAddAdjustmentsForm,
    (state, { payload: { ledgerItemId, accountId } }) =>
      produce(state, (draftState) => {
        draftState.addAdjustmentsDrawerOpen =
          !draftState.addAdjustmentsDrawerOpen;
        draftState.addAdjustmentsForm = {
          accountId,
          ledgerItemId,
        };
      })
  )
  .handleAction(
    updateAddAdjustmentsForm,
    (state, { payload: { ledgerItemId, accountId, adjustments } }) =>
      produce(state, (draftState) => {
        draftState.addAdjustmentsForm = {
          accountId,
          adjustments,
          ledgerItemId,
        };
      })
  )
  .handleAction(payableAdjustmentDeleteAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      const updatedAdjustments = [...draftState.adjustments];
      const updatedPayables = [...draftState.rows];

      const found = updatedAdjustments.findIndex((bc) => bc.id === payload.id);

      if (found < 0) {
        return draftState;
      }

      const payablesIndex = updatedPayables.findIndex(
        (bc) => bc.id === updatedAdjustments[found].ledgerItemId
      );

      updatedPayables[payablesIndex] = {
        ...draftState.rows[payablesIndex],
        adjustment:
          Number(updatedPayables?.[payablesIndex].adjustment) -
          updatedAdjustments[found].amount,
        totalPayout:
          Number(updatedPayables?.[payablesIndex].totalPayout) -
          updatedAdjustments[found].amount,
      };
      updatedAdjustments.splice(found, 1);
      draftState.adjustments = updatedAdjustments;
      draftState.rows = updatedPayables;
    })
  )
  .handleAction(deletePayableAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.rows = draftState.rows.filter((row) => row.id !== payload.id);
    })
  )
  .handleAction(createOneOffLedgerItemRequestAction.success, (state) =>
    produce(state, (draftState) => {
      draftState.createOneOffDrawerOpen = !draftState.createOneOffDrawerOpen;
      draftState.haveAllPayablesBeenRequested = false;
    })
  )
  .handleAction(payableEventsByIdRequestAction.success, (state, { payload }) =>
    produce(state, (draftState) => {
      draftState.eventsById[payload[0].ledgerItemId] = payload;
    })
  );
